import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Table } from "react-bootstrap";
import api, { deactivateEntityClient } from "../services/api";
import ManagementList from "../components/ManagementList";
import { formatDate } from "../lib/utils/helper";
import { LIMIT } from "../lib/utils/constant";
import { usePermissions } from "../Hooks/usePermission";
import { PERMISSIONS } from "../lib/utils/permissions";
import PermissionMessage from "../components/ErrorMessage/PermissionMessage";
const EntityManagement = () => {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [counts, setCounts] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("A");
  const [itemsPerPage, setItemsPerPage] = useState(LIMIT[0].value);

  const [showClientsModal, setShowClientsModal] = useState(false);
  const [selectedEntityName, setSelectedEntityName] = useState("");
  const [selectedClients, setSelectedClients] = useState([]);
  const { can } = usePermissions();
  const userRoleCode = JSON.parse(localStorage.getItem('user'))?.role?.code;
  const canViewEntity = can(PERMISSIONS.ENTITY_READ, userRoleCode);
  const canCreateEntity = can(PERMISSIONS.ENTITY_CREATE,userRoleCode);
  const canUpdateEntity = can(PERMISSIONS.ENTITY_UPDATE,userRoleCode);
  const canDeactivateEntity = can(PERMISSIONS.ENTITY_DEACTIVATE,userRoleCode);

  const columns = useMemo(
    () => [
      { header: "ENTITY", accessor: "name", align: "center" },
      {
        header: "CLIENT",
        accessor: "clientCount",
        align: "center",
        render: (item) => {
          const clients = Array.isArray(item?.clients) ? item.clients : [];
          const count = clients.length;

          if (count === 0) return 0;

          return (
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={() => {
                setSelectedEntityName(item?.name || "Clients");
                setSelectedClients(clients);
                setShowClientsModal(true);
              }}
            >
              {count}
            </Button>
          );
        },
      },
      {
        header: "ENROLLED PATIENTS",
        accessor: "enrolledPatients",
        align: "center",
      },
      { header: "LAST UPDATED", accessor: "updatedAtDisplay", align: "center" },
      { header: "UPDATED BY", accessor: "updatedByDisplay", align: "center" },
      { header: "ACTION", accessor: "action", align: "center" },
    ],
    [],
  );

  const handleLimitChange = (e) => {
    setItemsPerPage(e.target.value);
  };

  // 4. Entity Management
  const fetchEntityLists = async () => {
    const payload = {
      start: null,
      end: null,
      page: page,
      limit: itemsPerPage,
      status: statusFilter,
    };
    try {
      setLoading(true);
      const response = await api.post("/v1/entities/entity/list", payload);
      const data = response?.data?.data;
      const resolvedTotalPages = Number(data?.pagination?.pages) || 1;
      setTotalPages(resolvedTotalPages);
      setEntities(data);
      setCounts(data?.counts || data?.statusCounts || null);
    } catch (error) {
      console.error("Error fetching entity lists:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEntities = (entities?.items || [])
    .map((entity) => {
      const clients = Array.isArray(entity?.clients) ? entity.clients : [];
      const updatedBy = entity?.updatedBy;
      const updatedByDisplay =
        updatedBy && typeof updatedBy === "object"
          ? [updatedBy?.firstName, updatedBy?.lastName]
              .filter(Boolean)
              .join(" ") ||
            updatedBy?.email ||
            updatedBy?.id ||
            "-"
          : updatedBy || "-";
      return {
        ...entity,
        clientCount: clients.length,
        enrolledPatients: entity?.customerCount || "-",
        updatedAtDisplay: formatDate(entity?.updatedAt),
        updatedByDisplay,
      };
    })
    .filter((entity) => {
      const clients = Array.isArray(entity?.clients) ? entity.clients : [];
      if (!searchTerm) return true;

      const lowerTerm = searchTerm.toLowerCase();

      const matchesEntity =
        (entity?.name &&
          String(entity.name).toLowerCase().includes(lowerTerm)) ||
        (entity?.abbreviation &&
          String(entity.abbreviation).toLowerCase().includes(lowerTerm));

      const matchesClients = clients.some((c) => {
        const n = c?.name ? String(c.name).toLowerCase() : "";
        const a = c?.abbreviation ? String(c.abbreviation).toLowerCase() : "";
        return n.includes(lowerTerm) || a.includes(lowerTerm);
      });

      return matchesEntity || matchesClients;
    });
  const handleTabChange = (tabKey) => {
    let apiStatus = "A";
    if (tabKey === "active") apiStatus = "A";
    if (tabKey === "pending") apiStatus = "DF";
    if (tabKey === "deactivated") apiStatus = "D";
    if (tabKey === "all") apiStatus = "ALL";

    setStatusFilter(apiStatus);
    setPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    // No need to reset page for client-side filtering of current page data
  };
  const handleDeactivate = async (id, reason) => {
    try {
      await deactivateEntityClient(id, reason);
      fetchEntityLists();
    } catch (err) {
      console.error("Error deactivating entity:", err);
    }
  };
  useEffect(() => {
    fetchEntityLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, itemsPerPage]);
  const isDeactivateDisabled = (entity) => entity.clientCount > 0;
  return (
    <>
      {canViewEntity ? <ManagementList
        title="Entities"
        data={filteredEntities}
        columns={columns}
        addNewLink="/entity/new"
        editLinkBase="/entity/edit"
        onDeactivate={handleDeactivate}
        disabled={isDeactivateDisabled}
        isServerSide={true}
        isLoading={loading}
        serverPage={page}
        serverTotalPages={totalPages}
        serverCounts={counts}
        onPageChange={(newPage) => setPage(newPage)}
        onTabChange={handleTabChange}
        hiddenTabs={["pending"]}
        onSearch={handleSearch}
        limit={itemsPerPage}
        handleLimitChange={handleLimitChange}
        deactivationType="entity"
        canCreate={canCreateEntity}
        canUpdate={canUpdateEntity}
        canDeactivate={canDeactivateEntity}
        isAdminDeactivateDisabled={()=>{return false}}
      /> : <PermissionMessage  />}

      <Modal
        show={showClientsModal}
        onHide={() => setShowClientsModal(false)}
        centered
        size="lg"
      >
        <Modal.Header
          style={{ backgroundColor: "#0B5798", color: "white" }}
          closeButton
        >
          <Modal.Title>{selectedEntityName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h6 className="text-center mb-3 text-primary">Active Clients</h6>
          <Table bordered hover responsive className="mb-0">
            <thead className="bg-light">
              <tr>
                <th className="text-center">Name</th>
                <th className="text-center">Abbreviation</th>
              </tr>
            </thead>
            <tbody>
              {selectedClients?.length ? (
                selectedClients.map((c) => (
                  <tr key={c?.id || `${c?.name}-${c?.abbreviation}`}>
                    <td className="text-center">{c?.name || "-"}</td>
                    <td className="text-center">{c?.abbreviation || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2} className="text-center text-muted py-3">
                    No clients found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => setShowClientsModal(false)}>
            Ok
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EntityManagement;
